import React from 'react';
import {
    Badge,
    HStack,
    Text,
    Tooltip,
    Portal,
    Icon,
} from '@chakra-ui/react';
import { FaClock } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import { useGetSubscriptionStatusQuery } from '../../services/subscriptionService';
import { SubscriptionStatus } from '../../types';

interface SubscriptionBadgeProps {
    showTrialCountdown?: boolean;
    size?: 'sm' | 'md';
}

export const SubscriptionBadge: React.FC<SubscriptionBadgeProps> = ({
    showTrialCountdown = true,
    size = 'sm',
}) => {
    const { t } = useTranslation();
    const { data: subscription, isLoading } = useGetSubscriptionStatusQuery();

    if (isLoading || !subscription || subscription.isStripeExempt) {
        return null;
    }

    const getBadgeColor = () => {
        switch (subscription.status) {
            case SubscriptionStatus.Active:
                return 'green';
            case SubscriptionStatus.Trialing:
                return subscription.daysLeftInTrial && subscription.daysLeftInTrial <= 3 ? 'orange' : 'blue';
            case SubscriptionStatus.Free:
                return 'gray';
            case SubscriptionStatus.PastDue:
                return 'orange';
            case SubscriptionStatus.Canceled:
            case SubscriptionStatus.Expired:
                return 'red';
            default:
                return 'gray';
        }
    };

    const getPlanLabel = () => {
        if (!subscription.planType) return '';
        return t(`subscriptions.accountTypes.${subscription.planType}`);
    };

    const isTrialing = subscription.status === SubscriptionStatus.Trialing;
    const showCountdown = showTrialCountdown && isTrialing && subscription.daysLeftInTrial !== null;

    const tooltipLabel = isTrialing
        ? t('subscription.badge.trial_tooltip', { days: subscription.daysLeftInTrial })
        : t('subscription.badge.plan_tooltip', { plan: getPlanLabel() });

    return (
        <Tooltip.Root>
            <Tooltip.Trigger asChild>
                <HStack gap={1}>
                    <Badge
                        colorPalette={getBadgeColor()}
                        fontSize={size === 'sm' ? '10px' : 'xs'}
                        px={2}
                        py={0.5}
                        borderRadius="full"
                        textTransform="capitalize"
                    >
                        {getPlanLabel()}
                    </Badge>
                    {showCountdown && (
                        <Badge
                            colorPalette={subscription.daysLeftInTrial && subscription.daysLeftInTrial <= 3 ? 'orange' : 'blue'}
                            fontSize={size === 'sm' ? '10px' : 'xs'}
                            px={2}
                            py={0.5}
                            borderRadius="full"
                            variant="subtle"
                        >
                            <HStack gap={1}>
                                <Icon as={FaClock} boxSize={2} />
                                <Text>{subscription.daysLeftInTrial}d</Text>
                            </HStack>
                        </Badge>
                    )}
                </HStack>
            </Tooltip.Trigger>
            <Portal>
                <Tooltip.Positioner>
                    <Tooltip.Content>
                        <Tooltip.Arrow><Tooltip.ArrowTip /></Tooltip.Arrow>
                        {tooltipLabel}
                    </Tooltip.Content>
                </Tooltip.Positioner>
            </Portal>
        </Tooltip.Root>
    );
};
